import { h, createApp, configureCompat } from 'vue';
import { setupFaroVueErrorHandler, pushPageLoadPhase, bindFaroToRouter, initializeFaroIfEnabled } from '@/plugins/faro'; // Initialize Faro monitoring, import error handler setup function
import '@/plugins/posthog'; // Initialize PostHog analytics
import AppEntry from '@/AppEntry.vue';
import TranslationProvider from '@/TranslationProvider.vue';
import store from '@/store';
import { AuthProvider } from '@/auth';
import { VueQueryPlugin, QueryClient } from '@tanstack/vue-query';
import type { AxiosError } from 'axios';
import { isGatewayTimeout } from '@/utils/errorMessage';
import { bootstrapPlugin } from '@/plugins/bootstrap';
import { fontAwesomePlugin } from '@/plugins/fontAwesome';
import { isoCountryPlugin } from '@/plugins/isoCountries';
import { i18nPlugin } from './i18n';
import { focusOnInsertPlugin } from '@/plugins/focusOnInsert';
import { getPrimeVuePlugin } from '@/plugins/primeVue';
import compatConfig from '../compatConfig';
import { agGridPlugin } from '@/plugins/agGrid';
import { amchartsPlugin } from '@/plugins/amcharts';
import { getPluginsForEnv, getRouterForEnv, notifyAppMountedForEnv } from './plugins/appLevelDeps';
import { globalComponentsPlugin } from '@/plugins/globalComponents';

import '@/styles/fonts.ts';
import '@/styles/theme.scss';
import { currentNavigation } from './plugins/pageLoadNavigation';

/**
 * performance.now() is milliseconds since performance.timeOrigin, which is the same t=0 as the Chrome Performance panel
 * (navigation start: prompt, request, HTML, download, parse, eval).
 *
 * So this corresponds to the time duration:
 * - from when the user hits reload
 * - until every eager import in `Main.ts` is evaluated
 */
const modulesEvaluatedAt = performance.now();
performance.mark('boot:modules_evaluated');

const app = createApp({
  render: () => h(TranslationProvider, [h(AuthProvider, [h(AppEntry)])]),
});

window.graphs = {};
window.v5graphs = {};
window.titles = {};
window.subtitles = {};
window.legends = {};
window.footers = {};

app.use(VueQueryPlugin, {
  queryClient: new QueryClient({
    defaultOptions: {
      queries: {
        retry: (failureCount: number, error) => {
          if (isGatewayTimeout(error)) {
            return false;
          }

          if (failureCount >= 3) return false;

          if (((error as AxiosError).response?.status as number) >= 500) return false;

          return true;
        },
        staleTime: 20 * 1000,
      },
    },
  }),
  enableDevtoolsV6Plugin: true,
});

app.use(store);

const router = await getRouterForEnv();
void initializeFaroIfEnabled();
bindFaroToRouter(router);
app.use(router);
// Set up Grafana Faro app error handler
setupFaroVueErrorHandler(app);

app.use(bootstrapPlugin);
app.use(await getPrimeVuePlugin());

app.use(fontAwesomePlugin);
app.use(isoCountryPlugin);
app.use(i18nPlugin);
app.use(focusOnInsertPlugin);
app.use(agGridPlugin);
app.use(globalComponentsPlugin);
app.use(amchartsPlugin);

for (const plugin of await getPluginsForEnv()) {
  app.use(plugin);
}

app.directive('visible', function (el, binding) {
  el.style.visibility = binding.value ? 'visible' : 'hidden';
});

// Configure compatibility mode for the app
// casting type as we are using flag 'COMPILER_NATIVE_TEMPLATE'
configureCompat(compatConfig as Parameters<typeof configureCompat>[0]);

// WAA-10259: Disabled due to performance issues with Firefox. Potentially re-enable when upgrading to vue3.
app.config.performance = false;

app.mount('#app');
await notifyAppMountedForEnv(router);

const mountedAt = performance.now();
currentNavigation.mountedAt = mountedAt;
performance.mark('boot:mounted');

pushPageLoadPhase('boot:mounted', mountedAt, { is_cold_load: 'true' });
pushPageLoadPhase('boot:modules_evaluated', modulesEvaluatedAt, { is_cold_load: 'true' });

pushPageLoadPhase('seg:js_eval', modulesEvaluatedAt, { is_cold_load: 'true' });
pushPageLoadPhase('seg:vue_boot', mountedAt - modulesEvaluatedAt, { is_cold_load: 'true' });
